import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';
import {
  applyAnalyticsPendingLoading,
  markAnalyticsFetchKey,
} from '../../utils/analyticsQuery';
import dayjs from 'dayjs';

export const getAnalyticsOverviewStats = createAsyncThunk(
  'analyticsOverviewStats/getAnalyticsOverviewStats',
  async ({ range = '7d', startDate = null, endDate = null, group_id }, { getState, rejectWithValue }) => {
    try {
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;
      const business_id = loginToken?.response?.user?.business_id;

      // Map range aliases to standard values
      let effectiveRange = range;
      if (range === '365d') {
        effectiveRange = '1y';
      } else if (range === '1d') {
        effectiveRange = 'today';
      }
      // 'today' and 'yesterday' are already handled as they are

      // Build query params based on whether custom dates are provided
      let queryParams = '';
      if (startDate && endDate) {
        // Custom date range
        queryParams = `from=${startDate}&to=${endDate}&range=custom`;
      } else {
        // Predefined range
        queryParams = `range=${effectiveRange}`;
      }
      if (group_id) {
        queryParams += `&group_id=${encodeURIComponent(group_id)}`;
      }

      const response = await axios.get(
        `${ROOT_URL}/analytics/overview/${business_id}?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  data: null,
  lastFetchKey: null,
  currentRange: '7d', // 'today', 'yesterday', '7d', '30d', '365d', '1y', or 'custom'
  dateRange: {
    startDate: dayjs().subtract(6, 'day').format('YYYY-MM-DD'), // Changed from 7 to 6
    endDate: dayjs().format('YYYY-MM-DD')
  },
  currentGroup: 'All Groups'
};

const analyticsOverviewStatsSlice = createSlice({
  name: 'analyticsOverviewStats',
  initialState,
  reducers: {
    resetAnalyticsOverviewStatsState: (state) => {
      state.loading = false;
      state.error = null;
      state.data = null;
      state.lastFetchKey = null;
      state.currentRange = '7d';
      state.dateRange = {
        startDate: dayjs().subtract(6, 'day').format('YYYY-MM-DD'), // Changed from 7 to 6
        endDate: dayjs().format('YYYY-MM-DD')
      };
      state.currentGroup = 'All Groups';
    },
    setCurrentRange: (state, action) => {
      state.currentRange = action.payload;
    },
    setDateRange: (state, action) => {
      state.dateRange = {
        startDate: action.payload.startDate,
        endDate: action.payload.endDate
      };
      // Determine the range type
      const start = dayjs(action.payload.startDate);
      const end = dayjs(action.payload.endDate);
      const today = dayjs().startOf('day');
      const yesterday = today.subtract(1, 'day');

      const isToday = start.isSame(today, 'day') && end.isSame(today, 'day');
      const isYesterday = start.isSame(yesterday, 'day') && end.isSame(yesterday, 'day');
      const isLast7 = start.isSame(today.subtract(6, 'day'), 'day') && end.isSame(today, 'day');
      const isLast30 = start.isSame(today.subtract(29, 'day'), 'day') && end.isSame(today, 'day');
      const isLast12M = start.isSame(today.subtract(12, 'month').add(1, 'day'), 'day') && end.isSame(today, 'day');

      if (isToday) {
        state.currentRange = 'today';
      } else if (isYesterday) {
        state.currentRange = 'yesterday';
      } else if (isLast7) {
        state.currentRange = '7d';
      } else if (isLast30) {
        state.currentRange = '30d';
      } else if (isLast12M) {
        state.currentRange = '1y';
      } else {
        state.currentRange = 'custom';
      }
    },
    setCurrentGroup: (state, action) => {
      state.currentGroup = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAnalyticsOverviewStats.pending, (state, action) => {
        applyAnalyticsPendingLoading(state, action.meta?.arg);
        state.error = null;
      })
      .addCase(getAnalyticsOverviewStats.fulfilled, (state, action) => {
        state.loading = false;
        markAnalyticsFetchKey(state, action.meta?.arg);
        state.data = action.payload.data;
        state.error = null;
      })
      .addCase(getAnalyticsOverviewStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.data = null;
      });
  }
});

export const {
  resetAnalyticsOverviewStatsState,
  setCurrentRange,
  setDateRange,
  setCurrentGroup
} = analyticsOverviewStatsSlice.actions;

export default analyticsOverviewStatsSlice.reducer;